import { ref, computed, reactive } from '@vue/composition-api';
import AITranslationService from '@/services/AITranslationService';
import { bulkUpdateMultiLocaleTranslations } from '@/http/translations';

/**
 * Composable for AI translation functionality
 * Provides reactive state and methods for translating content with AI
 */
export function useAITranslation() {
  // Reactive state
  const isTranslating = ref(false);
  const isSingleTranslating = ref(false);
  const showTranslationPreview = ref(false);
  const proposedTranslations = ref({});
  const lastError = ref(null);
  const translationProgress = reactive({
    current: 0,
    total: 0,
    currentLanguage: '',
    message: '',
  });

  // Computed properties
  const hasProposedTranslations = computed(() => Object.keys(proposedTranslations.value).length > 0);

  const translationCount = computed(() => Object.keys(proposedTranslations.value).length);

  /**
   * Translate all translations in a category using AI
   * @param {Object} translations - Current translations object
   * @param {Function} getTranslationsForKey - Function to get translations for a key
   * @param {Array} availableLanguages - Available language configurations
   * @param {boolean} missingOnly - If true, only translate keys with missing translations
   * @returns {Promise<boolean>} Success status
   */
  const translateAllWithAI = async (translations, getTranslationsForKey, availableLanguages, missingOnly = false) => {
    if (isTranslating.value || Object.keys(translations).length === 0) {
      return false;
    }

    isTranslating.value = true;
    lastError.value = null;

    try {
      const translationsToProcess = {};

      // Get target languages (exclude Polish)
      const targetLanguages = availableLanguages.filter((lang) => lang.lang !== 'pl').map((lang) => lang.locale);

      // Collect keys with Polish translations
      Object.keys(translations).forEach((key) => {
        const polishText = getPolishTranslation(key, getTranslationsForKey);
        if (polishText && polishText.trim()) {
          // If missingOnly is true, check if any target language is missing
          if (missingOnly) {
            const allTranslations = getTranslationsForKey(key);
            const hasMissingTranslations = targetLanguages.some((locale) => {
              const translation = allTranslations[locale];
              return !translation || !translation.trim();
            });

            if (hasMissingTranslations) {
              translationsToProcess[key] = polishText;
            }
          } else {
            // Include all keys with Polish translations
            translationsToProcess[key] = polishText;
          }
        }
      });

      if (Object.keys(translationsToProcess).length === 0) {
        const errorMessage = missingOnly
          ? 'Nie znaleziono kluczy z brakującymi tłumaczeniami do przetłumaczenia.'
          : 'Nie znaleziono polskich tłumaczeń do przetłumaczenia.';
        throw new Error(errorMessage);
      }

      // Call AI translation service with progress tracking
      const aiTranslations = await AITranslationService.translateMultiple(
        translationsToProcess,
        targetLanguages,
        (stepName, progress, isComplete) => {
          // Update progress for chunked processing
          const totalKeys = Object.keys(translationsToProcess).length;
          const modeText = missingOnly ? 'brakujących' : 'wszystkich';
          translationProgress.value = {
            current: Math.round(progress * totalKeys),
            total: totalKeys,
            currentLanguage: stepName,
            message:
              totalKeys > 50
                ? `Przetwarzanie ${modeText} w częściach: ${stepName}`
                : `Tłumaczenie ${modeText}: ${stepName}`,
          };
        },
      );

      proposedTranslations.value = aiTranslations;
      showTranslationPreview.value = true;

      return true;
    } catch (error) {
      console.error('Error translating with AI:', error);
      lastError.value = error.message || 'Wystąpił błąd podczas tłumaczenia z AI.';
      return false;
    } finally {
      isTranslating.value = false;
    }
  };

  /**
   * Translate a single key using AI
   * @param {string} key - Translation key
   * @param {Function} getTranslationsForKey - Function to get translations for a key
   * @param {Array} availableLanguages - Available language configurations
   * @returns {Promise<boolean>} Success status
   */
  const translateSingleKeyWithAI = async (key, getTranslationsForKey, availableLanguages) => {
    if (isSingleTranslating.value || !key) {
      return false;
    }

    const polishText = getPolishTranslation(key, getTranslationsForKey);

    if (!polishText || !polishText.trim()) {
      lastError.value = 'Aby przetłumaczyć z AI, najpierw wprowadź polskie tłumaczenie.';
      return false;
    }

    isSingleTranslating.value = true;
    lastError.value = null;

    try {
      // Get target languages (exclude Polish)
      const targetLanguages = availableLanguages.filter((lang) => lang.lang !== 'pl').map((lang) => lang.locale);

      // Call AI translation service for single key
      const aiTranslations = await AITranslationService.translateMultiple({ [key]: polishText }, targetLanguages);

      proposedTranslations.value = aiTranslations;
      showTranslationPreview.value = true;

      return true;
    } catch (error) {
      console.error('Error translating single key with AI:', error);
      lastError.value = error.message || 'Wystąpił błąd podczas tłumaczenia z AI.';
      return false;
    } finally {
      isSingleTranslating.value = false;
    }
  };

  /**
   * Accept and save AI-proposed translations
   * @param {Object} acceptedTranslations - Translations accepted by user
   * @returns {Promise<boolean>} Success status
   */
  const acceptTranslations = async (acceptedTranslations) => {
    try {
      // Call the bulk update API
      await bulkUpdateMultiLocaleTranslations(acceptedTranslations);

      // Clear state
      showTranslationPreview.value = false;
      proposedTranslations.value = {};
      lastError.value = null;

      return true;
    } catch (error) {
      console.error('Error updating translations:', error);
      lastError.value = 'Wystąpił błąd podczas aktualizacji tłumaczeń.';
      return false;
    }
  };

  /**
   * Cancel AI translation preview
   */
  const cancelTranslations = () => {
    showTranslationPreview.value = false;
    proposedTranslations.value = {};
    lastError.value = null;
  };

  /**
   * Get Polish translation for a key
   * @param {string} key - Translation key
   * @param {Function} getTranslationsForKey - Function to get translations for a key
   * @returns {string} Polish translation
   */
  const getPolishTranslation = (key, getTranslationsForKey) => {
    const allTranslations = getTranslationsForKey(key);
    return allTranslations['pl-PL'] || '';
  };

  /**
   * Check if AI translation is available for the current environment
   * @returns {Promise<boolean>} Whether AI translation is available
   */
  const isAITranslationAvailable = async () => {
    try {
      return await AITranslationService.isAvailable();
    } catch (error) {
      console.error('Error checking AI translation availability:', error);
      return false;
    }
  };

  /**
   * Get supported languages from AI service
   * @returns {Object} Supported languages
   */
  const getSupportedLanguages = () => {
    return AITranslationService.getSupportedLanguages();
  };

  /**
   * Reset all AI translation state
   */
  const resetState = () => {
    isTranslating.value = false;
    isSingleTranslating.value = false;
    showTranslationPreview.value = false;
    proposedTranslations.value = {};
    lastError.value = null;
    translationProgress.value = {
      current: 0,
      total: 0,
      currentLanguage: '',
      message: '',
    };
  };

  return {
    // State
    isTranslating,
    isSingleTranslating,
    showTranslationPreview,
    proposedTranslations,
    lastError,
    translationProgress,

    // Computed
    hasProposedTranslations,
    translationCount,

    // Methods
    translateAllWithAI,
    translateSingleKeyWithAI,
    acceptTranslations,
    cancelTranslations,
    getPolishTranslation,
    isAITranslationAvailable,
    getSupportedLanguages,
    resetState,
  };
}
